import React from "react";
import _ from "lodash";

import InputSelectField from "./InputSelectField";
import InputDateField from "./InputDateField";

import { ParkerMeta } from "../../Entities/Parker";
import { RateMeta } from "../../Entities/Rate";
import { FeeMeta, FeeMetaInput } from "../../Entities/Fee";
import LineItemEntry from "../../Entities/LineItemEntry";

import useStandardLineItemMetaOptions from "../../Hooks/useStandardLineItemMetaOptions";
import featureFlags from "../../featureFlags";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import "./InvoiceLineItemTableRow.scss";

const InvoiceLineItemTableRowStandardFields = function ({
  lineItem,
  subscription,
  rates,
  inputId,
  newMode,
  adjustmentMode,
  fieldConfiguration,
  allowMetadataEditing,
  isCarryover,
  onChange,
  _onLineItemEntryChange,
  lineItemFeeTypes,
  id,
  taxExempt,
  fees,
  updateSelectedFees,
  selectedFees,
}) {
  const {
    parkerInputOptions,
    rateInputOptions,
    feeInputOptions,
    selectedStartDateKey,
    selectedEndDateKey,
  } = useStandardLineItemMetaOptions({
    lineItem,
    subscription,
    rates,
    newMode,
    inputId,
    allowMetadataEditing,
  });
  const allFees = (rates || []).reduce(
    (allFees, rate) => [...allFees, ...rate.allFees],
    []
  );
  const feeType = lineItemFeeTypes[id];

  const _onChange = function (inputId) {
    return (value) => {
      const inputIds = Array.isArray(inputId) ? inputId : [inputId];
      const values = Array.isArray(value) ? value : [value];
      const _lineItem = _.cloneDeep(lineItem);

      inputIds.forEach((id, i) => {
        _lineItem.fields[id].value = values[i];
      });

      onChange(_lineItem);
    };
  };

  const _onParkerMetaAttributeChange = function (inputId) {
    return (value) => {
      const parkerValue = lineItem.fields.parker.value;
      const parkerMeta = parkerValue
        ? _.cloneDeep(lineItem.fields.parker.value)
        : new ParkerMeta();
      parkerMeta.fields[inputId].value = value;

      let rateMeta;
      switch (inputId) {
        case "id":
          const parker = subscription.fields.parkers.value.find(
            (parker) => parker.fields.id.value === value
          );
          if (parker) {
            parkerMeta.fields = {
              ..._.cloneDeep(parkerMeta.fields),
              ..._.cloneDeep(parker.fields),
            };
            const rateValue = lineItem.fields.rate.value;
            rateMeta = rateValue
              ? _.cloneDeep(lineItem?.fields.rate.value)
              : new RateMeta();
            rateMeta.fields["id"].value = parker.fields.rate_id.value;
          }

          _onChange(["parker", "rate"])([parkerMeta, rateMeta]);
          break;
        default:
          _onChange("parker")(parkerMeta);
      }
    };
  };

  const _onRateMetaAttributeChange = function (inputId) {
    return (value) => {
      const rateValue = lineItem.fields.rate.value;
      const rateMeta = rateValue
        ? _.cloneDeep(lineItem.fields.rate.value)
        : new RateMeta();
      rateMeta.fields[inputId].value = value;

      _onChange("rate")(rateMeta);
    };
  };

  const _onFeeMetaAttributeChange = function (inputId) {
    return (value) => {
      const feeValue = lineItem.fields.fee.value;
      const feeMeta = feeValue ? _.cloneDeep(feeValue) : new FeeMeta();
      feeMeta.fields[inputId].value = value;

      switch (inputId) {
        case "id":
          const selectedFee = allFees.find(
            (fee) => value === fee.fields.id.value
          );
          if (selectedFee) {
            for (const field in FeeMetaInput) {
              feeMeta.fields[field].value = selectedFee.fields[field].value;
            }

            const entries = lineItem.fields.entries.value;
            if (Array.isArray(entries)) {
              const entry = entries.length
                ? _.cloneDeep(entries[entries.length - 1])
                : new LineItemEntry();

              entry.fields.type.value = entry.fields.type.value ?? "CHARGE";
              entry.fields.amount.value = selectedFee.fields.price.value;
              entry.fields.amount_tax_1.value = !taxExempt
                ? selectedFee.fields.tax_1.value
                : 0;
              entry.fields.amount_tax_2.value = !taxExempt
                ? selectedFee.fields.tax_2.value
                : 0;
              entry.fields.amount_tax_3.value = !taxExempt
                ? selectedFee.fields.tax_3.value
                : 0;
              entry.fields.amount_tax_4.value = !taxExempt
                ? selectedFee.fields.tax_4.value
                : 0;
              entry.fields.name.value = selectedFee.name.value

              setTimeout(() => {
                const ident = entries.length === 0 ? 0 : entries.length - 1;
                _onLineItemEntryChange(ident, entry);
              }, 0);
            }
          }
          break;
        default:
      }

      _onChange("fee")(feeMeta);
    };
  };

  return (
    <>
      {featureFlags.CARRYOVER_RATE && isCarryover && (
        <div className="parkchirp--invoice-line-item-table--line-item-rate-type-column parkchirp--invoice-line-item-table--cell">
          <InputSelectField
            inputId={`${inputId}--line-item-rate-type`}
            options={fieldConfiguration.lineItem.line_item_rate_type.options}
            value={lineItem.fields.line_item_rate_type.value}
            onChange={_onChange("line_item_rate_type")}
            editMode={allowMetadataEditing && newMode}
          />
        </div>
      )}

      <div
        className={`parkchirp--invoice-line-item-table--fee-type-column parkchirp--invoice-line-item-table--cell 
        ${newMode && feeType?.showFeeTypeError ? "fee-type-error" : ""}`}
      >
        {newMode ? (
          <>
            <Select
              style={{ width: "95%" }}
              onChange={(e) => updateSelectedFees(e.target.value, id)}
            >
              {fees.map((f, index) => (
                <MenuItem value={index}>{f.feeName}</MenuItem>
              ))}
            </Select>
            {newMode && feeType?.showFeeTypeError && (
              <span>A Fee must be selected</span>
            )}
          </>
        ) : (
          <span>
            {lineItem.fields.fee?.value?.fields?.name.value ? lineItem.fields.fee?.value?.fields?.name.value : lineItem.fields.fee?.value?.fields?.type.value + ' Fee'}
          </span>
        )}
      </div>
      {selectedFees[id] &&
      selectedFees[id].calculationType === "DOLLAR_PER_PARKER" ? (
        <div className="parkchirp--invoice-line-item-table--parker-name-column parkchirp--invoice-line-item-table--cell">
          <InputSelectField
            inputId={`${inputId}--parker`}
            options={parkerInputOptions}
            value={lineItem.fields.parker.value?.fields.id.value || null}
            onChange={_onParkerMetaAttributeChange("id")}
            editMode={allowMetadataEditing && newMode}
          />
        </div>
      ) : lineItem.fields.parker.value ? (
        <div className="parkchirp--invoice-line-item-table--parker-name-column parkchirp--invoice-line-item-table--cell">
          {lineItem.fields.parker.value.fields.first_name.value + ' ' + lineItem.fields.parker.value.fields.last_name.value}
        </div>
      ) : (
        <div className="parkchirp--invoice-line-item-table--parker-name-column parkchirp--invoice-line-item-table--cell">
      </div>
      )}

      {selectedFees[id] &&
      selectedFees[id].calculationType === "DOLLAR_PER_PARKER" ? (
        <div className="parkchirp--invoice-line-item-table--rate-name-column parkchirp--invoice-line-item-table--cell">
          <InputSelectField
            inputId={`${inputId}--rate`}
            options={rateInputOptions}
            value={lineItem.fields.rate.value?.fields.id.value || null}
            onChange={_onRateMetaAttributeChange("id")}
            editMode={
              newMode && lineItem.fields.parker.value?.fields.id.value === null
            }
          />
        </div>
      ) : lineItem.fields.rate.value ? (
        <div className="parkchirp--invoice-line-item-table--parker-name-column parkchirp--invoice-line-item-table--cell">
          {lineItem.fields.rate.value.fields.name.value}
        </div>
      ) : (
        <div className="parkchirp--invoice-line-item-table--parker-name-column parkchirp--invoice-line-item-table--cell">
      </div>
      )}
      {lineItem.fields.parker.value ?
      <div className="parkchirp--invoice-line-item-table--selected-date-range-column parkchirp--invoice-line-item-table--cell">
      <InputDateField
        inputId={`${inputId}--selected-start-date`}
        value={
          lineItem.fields.parker.value?.fields.selected_start_date.value ||
          null
        }
        key={selectedStartDateKey}
        editMode={allowMetadataEditing && (newMode || adjustmentMode)}
        onChange={_onParkerMetaAttributeChange("selected_start_date")}
      />
      <span>&nbsp;&ndash;&nbsp;</span>
      <InputDateField
        inputId={`${inputId}--selected-end-date`}
        value={
          lineItem.fields.parker.value?.fields.selected_end_date.value || null
        }
        key={selectedEndDateKey}
        editMode={allowMetadataEditing && (newMode || adjustmentMode)}
        onChange={_onParkerMetaAttributeChange("selected_end_date")}
      />
    </div>
      : <div className="parkchirp--invoice-line-item-table--selected-date-range-column parkchirp--invoice-line-item-table--cell">
        </div> 
    }
    </>
  );
};

export default InvoiceLineItemTableRowStandardFields;
